import React, { useEffect, useRef, useState } from 'react';

import { loadGoogleMaps } from '@/lib/googleMaps';
import { GSM_MAP_STYLE } from '@/lib/googleMapsStyle';
import { DispatchDetailBatch, tripKindOf } from '@/services/api/dispatches';

// Categorical palette tuned for the light GSM canvas: each color stays
// readable next to the others and against the map's near-monochrome base.
// 13 entries covers a typical Jaksel-day plan (~13 batches) before wrap;
// anything beyond cycles back to index 0, which is acceptable because the
// dispatcher reads batches in groups (per-rider trips) anyway.
export const BATCH_COLORS = [
  '#2563eb',
  '#16a34a',
  '#dc2626',
  '#f59e0b',
  '#9333ea',
  '#0891b2',
  '#ea580c',
  '#65a30d',
  '#db2777',
  '#475569',
  '#a16207',
  '#7c3aed',
  '#0d9488',
];

export const batchColor = (index: number): string =>
  BATCH_COLORS[((index % BATCH_COLORS.length) + BATCH_COLORS.length) %
    BATCH_COLORS.length];

// Held (DEFERRED) stops are marked in a warm neutral gray — deliberately
// OUTSIDE the vivid BATCH_COLORS palette (which even contains orange, our
// panel's deferred tint) so a held marker can never be mistaken for a routed
// batch. Rendered hollow + route-less, it reads as a "ghost" stop.
export const DEFERRED_COLOR = '#78716c';

// Same defensive coord guard as ZoneMap — Drizzle numerics can serialize as
// strings, and (0, 0) is the canonical "missing coord" sentinel we never want
// to actually render as a marker.
const isValidCoord = (lat: unknown, lng: unknown): boolean => {
  const nLat = typeof lat === 'number' ? lat : Number(lat);
  const nLng = typeof lng === 'number' ? lng : Number(lng);
  return (
    Number.isFinite(nLat) &&
    Number.isFinite(nLng) &&
    nLat >= -90 &&
    nLat <= 90 &&
    nLng >= -180 &&
    nLng <= 180 &&
    !(nLat === 0 && nLng === 0)
  );
};

const JAKARTA_CENTER = { lat: -6.1754, lng: 106.8272 };
const JAKARTA_ZOOM = 11;

// Teardrop pin shared with ZoneMap so the hub reads the same across surfaces.
const HUB_PIN_PATH =
  'M 0,0 C -6,-8 -12,-14 -12,-22 C -12,-28 -6,-32 0,-32 C 6,-32 12,-28 12,-22 C 12,-14 6,-8 0,0 z';

export interface DispatchMapHub {
  code: string;
  name: string;
  latitude: number;
  longitude: number;
  /** Shown in the hub pin's info window when known. */
  address?: string | null;
  phoneNumber?: string | null;
}

/**
 * Hub pin info window: the place itself plus what this dispatch does there —
 * how many dropoff trips leave it loaded and how many pickup trips end there,
 * and the riders involved.
 */
const buildHubTooltipHTML = (
  hub: DispatchMapHub,
  batches: DispatchDetailBatch[]
): string => {
  const dropTrips = batches.filter(
    (b) => tripKindOf(b.stops) === 'DROPOFF' || tripKindOf(b.stops) === 'MIXED'
  ).length;
  const pickupTrips = batches.filter(
    (b) => tripKindOf(b.stops) === 'PICKUP' || tripKindOf(b.stops) === 'MIXED'
  ).length;
  const riders = new Set(
    batches.map((b) => b.assignedRiderName).filter((n): n is string => !!n)
  );
  const stops = batches.reduce((n, b) => n + b.stops.length, 0);
  return `
  <div style="${IW_FONT} position:relative; width:272px; padding:12px 14px 12px; color:#171717;">
    ${iwClose()}
    <div style="display:flex; flex-wrap:wrap; gap:4px; margin-bottom:4px; padding-right:28px;">
      ${iwChip('Hub / Pitstop', 'hub', 'ri-map-pin-2-line')}
      ${[...riders].map((r) => iwChip(r, 'neutral', 'ri-user-3-line')).join('')}
    </div>
    <p style="margin:0; font-size:13px; font-weight:600; letter-spacing:-.01em; line-height:1.3;">${escapeHtml(hub.name)}</p>
    <p style="margin:2px 0 8px; ${IW_MONO} font-size:11px; color:#5c5c5c;">${escapeHtml(hub.code)}</p>
    ${hub.address ? iwLine(escapeHtml(hub.address), 'word-break:break-word;') : ''}
    ${
      hub.phoneNumber
        ? iwLine(`<i class="ri-phone-line" style="font-size:12px; vertical-align:-1px; margin-right:2px;"></i>${escapeHtml(hub.phoneNumber)}`, 'font-variant-numeric:tabular-nums;')
        : ''
    }
    <div style="margin-top:8px; padding-top:8px; border-top:1px solid rgba(23,23,23,.08);">
      ${iwLine(`<i class="ri-inbox-archive-line" style="font-size:12px; vertical-align:-1px; margin-right:4px; color:#23409f;"></i><b style="color:#171717; font-weight:600;">${dropTrips}</b> trip dropoff berangkat dari sini`)}
      ${iwLine(`<i class="ri-inbox-unarchive-line" style="font-size:12px; vertical-align:-1px; margin-right:4px; color:#116d61;"></i><b style="color:#171717; font-weight:600;">${pickupTrips}</b> trip pickup berakhir di sini`)}
      ${iwLine(`${batches.length} trip · ${stops} stop`, 'font-variant-numeric:tabular-nums; color:#9a9a9a;')}
    </div>
  </div>
`;
};

export interface DispatchMapProps {
  /** PLANNED batches with stops to render (routed, per-batch color). */
  batches: DispatchDetailBatch[];
  /**
   * DEFERRED batches. Their stops are drawn as route-less gray "ghost"
   * markers so ops can see WHERE the held goods are headed without them
   * competing with the routed batch overlays. Optional — omit to hide.
   */
  deferredBatches?: DispatchDetailBatch[];
  /** Stable index → color mapping, owned by the parent so chips match. */
  batchIndexById: Map<string, number>;
  hub: DispatchMapHub | null;
  selectedBatchId: string | null;
  onSelectBatch: (batchId: string | null) => void;
  /**
   * Fixed pixel height. Omit to let the map fill its parent (h-full) — the
   * default for the side-by-side dispatch layout where the parent owns the
   * height via a flex pane.
   */
  height?: number;
  /**
   * Inset padding (px) applied to fitBounds so markers stay visible despite
   * overlays. Right-heavy in the dispatch detail because the floating
   * sidebar covers the right edge of the map. Pass smaller values if you
   * remove the overlay.
   */
  fitPadding?: { top: number; right: number; bottom: number; left: number };
}

const DEFAULT_FIT_PADDING = { top: 40, right: 40, bottom: 40, left: 40 };

const escapeHtml = (s: string): string =>
  s.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// Pickup stops (rider collects, goods ride back to the hub) get a diamond so
// they read apart from the round dropoff markers at a glance. Unit-square
// path rotated 45°; `scale` sizes it to match SymbolPath.CIRCLE at 11.
const PICKUP_MARKER_PATH = 'M 0,-1 L 1,0 L 0,1 L -1,0 Z';

/**
 * Route-line symbols. Every trip gets direction arrows along the line, so a
 * drop run reads hub → stops and a pickup run stops → hub (its path is built
 * in that order, by the BE snapshot and the fallback alike). A pickup run
 * additionally draws as a dash pattern — the dash is itself a repeated
 * symbol, which is why the caller zeroes the base stroke for it.
 */
const routeIcons = (
  maps: any,
  color: string,
  pickupOnly: boolean,
  opacity: number
): any[] => {
  const arrow = {
    icon: {
      path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
      scale: 2.6,
      fillColor: color,
      fillOpacity: opacity,
      strokeColor: '#ffffff',
      // Explicit: a symbol's stroke opacity otherwise inherits the line's,
      // which is 0 on a dashed run.
      strokeOpacity: opacity,
      strokeWeight: 1,
    },
    offset: '40px',
    repeat: '110px',
  };
  if (!pickupOnly) return [arrow];
  const dash = {
    icon: {
      path: 'M 0,-1 0,1',
      strokeOpacity: opacity,
      strokeColor: color,
      scale: 2.5,
    },
    offset: '0',
    repeat: '12px',
  };
  return [dash, arrow];
};

/**
 * Line style for one batch route. A dashed (pickup) run keeps its base stroke
 * transparent — the dash pattern IS the line — so dimming/highlighting must
 * go through the icons' opacity, never through `strokeOpacity`. Every place
 * that touches a route's look goes through here so the two stay consistent.
 */
const routeStyle = (
  maps: any,
  color: string,
  dashed: boolean,
  opacity: number
) => ({
  strokeColor: color,
  strokeOpacity: dashed ? 0 : opacity,
  icons: routeIcons(maps, color, dashed, opacity),
});

/**
 * Stop info window: who goes there (rider), whose goods (clients), the place
 * and contact, then every waybill with its booking id — so ops can answer a
 * rider's or a client's question straight from the map.
 */
// ── Info-window building blocks — the same look as the sidebar's stop card
// (chips, 13px bold name, mono refs, 11px grey lines), so the map and the
// list read as one UI. Inline styles because InfoWindow content is raw HTML
// outside the Tailwind tree.
const IW_FONT = "font-family:'Plus Jakarta Sans',system-ui,sans-serif;";
const IW_MONO = "font-family:'JetBrains Mono',ui-monospace,Menlo,monospace;";
const iwChip = (
  text: string,
  tone: 'pickup' | 'dropoff' | 'neutral' | 'hub' | 'held',
  icon?: string
) => {
  const colors = {
    pickup: 'background:#e4fbf8; color:#116d61;',
    dropoff: 'background:#ebf1ff; color:#23409f;',
    neutral: 'background:#f5f5f5; color:#5c5c5c;',
    hub: 'background:#efeaf7; color:#431e7a;',
    held: 'background:#fff3eb; color:#9a4506;',
  }[tone];
  return `<span style="display:inline-flex; align-items:center; gap:4px; height:18px; padding:0 6px; border-radius:6px; font-size:10px; font-weight:600; white-space:nowrap; ${colors}">${
    icon ? `<i class="${icon}" style="font-size:11px;"></i>` : ''
  }${escapeHtml(text)}</span>`;
};
const iwLine = (html: string, extra = '') =>
  `<p style="margin:2px 0 0; font-size:11px; line-height:1.4; color:#5c5c5c; ${extra}">${html}</p>`;
// Our own close button (Google's header is disabled). Wired on `domready` by
// class — the content is an HTML string, so no React handler can attach.
const IW_CLOSE_CLASS = 'dispatch-iw-close';
const iwClose = () =>
  `<button type="button" class="${IW_CLOSE_CLASS}" aria-label="Tutup" title="Tutup"
     style="position:absolute; top:8px; right:8px; width:24px; height:24px; border:0; border-radius:6px; background:transparent; color:#7b7b7b; cursor:pointer; display:grid; place-items:center; padding:0;"
     onmouseover="this.style.background='#f5f5f5'; this.style.color='#171717';"
     onmouseout="this.style.background='transparent'; this.style.color='#7b7b7b';">
     <i class="ri-close-line" style="font-size:16px;"></i>
   </button>`;

const buildStopTooltipHTML = (args: {
  batchCode: string;
  sequence: number;
  pickup: boolean;
  /** Null on a DEFERRED (held) stop — no rider yet. */
  riderName: string | null;
  riderCode: string | null;
  clients: string[];
  contactName: string;
  address: string;
  phone: string | null;
  itemCount: number;
  totalWeightKg: number;
  computedStopTimeMin: number;
  legDistanceM: number | null;
  items: Array<{ waybill: string; bookingId?: string | null }>;
}): string => {
  const first = args.items[0];
  return `
  <div style="${IW_FONT} position:relative; width:272px; padding:12px 14px 12px; color:#171717;">
    ${iwClose()}
    <div style="display:flex; align-items:center; gap:8px; margin-bottom:8px; padding-right:28px;">
      <span style="${IW_MONO} font-size:11px; font-weight:600; color:#5e2aac;">${escapeHtml(args.batchCode)}</span>
      <span style="margin-left:auto; font-size:10px; font-weight:700; letter-spacing:.14em; text-transform:uppercase; color:#9a9a9a;">Stop ${args.sequence}</span>
    </div>
    <div style="display:flex; align-items:center; gap:6px; margin-bottom:8px; padding:6px 8px; border-radius:8px; background:${args.riderName ? '#f5f5f5' : '#fff3eb'};">
      <i class="${args.riderName ? 'ri-user-3-line' : 'ri-pause-circle-line'}" style="font-size:13px; color:${args.riderName ? '#5e2aac' : '#9a4506'};"></i>
      ${
        args.riderName
          ? `<span style="font-size:12px; font-weight:600; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${escapeHtml(args.riderName)}</span>${
              args.riderCode ? `<span style="${IW_MONO} font-size:10px; color:#5c5c5c; margin-left:auto;">${escapeHtml(args.riderCode)}</span>` : ''
            }`
          : `<span style="font-size:12px; font-weight:600; color:#9a4506;">Belum ada rider (ditahan)</span>`
      }
    </div>
    <div style="display:flex; flex-wrap:wrap; gap:4px; margin-bottom:4px;">
      ${args.pickup ? iwChip('Pickup', 'pickup', 'ri-inbox-unarchive-line') : iwChip('Dropoff', 'dropoff', 'ri-inbox-archive-line')}
      ${args.clients.map((c) => iwChip(c, 'neutral')).join('')}
    </div>
    ${
      args.contactName
        ? `<p style="margin:0; font-size:13px; font-weight:600; letter-spacing:-.01em; line-height:1.3; word-break:break-word;">${escapeHtml(args.contactName)}</p>`
        : ''
    }
    ${
      first
        ? `<p style="margin:2px 0 8px; ${IW_MONO} font-size:11px; font-variant-numeric:tabular-nums; display:flex; flex-wrap:wrap; gap:0 6px;">
             <span style="color:#5e2aac; font-weight:600;">${escapeHtml(first.waybill)}</span>
             ${first.bookingId ? `<span style="color:#9a9a9a;">·</span><span style="color:#5c5c5c;">${escapeHtml(first.bookingId)}</span>` : ''}
           </p>`
        : ''
    }
    ${iwLine(escapeHtml(args.address), 'word-break:break-word;')}
    ${
      args.phone
        ? iwLine(`<i class="ri-phone-line" style="font-size:12px; vertical-align:-1px; margin-right:2px;"></i>${escapeHtml(args.phone)}`, 'font-variant-numeric:tabular-nums;')
        : ''
    }
    ${iwLine(
      `${args.itemCount} item${args.itemCount === 1 ? '' : 's'} · ${args.totalWeightKg.toFixed(2)} kg · ${args.computedStopTimeMin} min${
        args.legDistanceM != null ? ` · ${(args.legDistanceM / 1000).toFixed(1)} km` : ''
      }`,
      'font-variant-numeric:tabular-nums;'
    )}
    ${
      args.items.length > 1
        ? `<div style="margin-top:8px; padding-top:8px; border-top:1px solid rgba(23,23,23,.08); display:flex; flex-direction:column; gap:3px;">${args.items
            .map(
              (it) =>
                `<span style="${IW_MONO} font-size:10.5px; color:#5c5c5c;">${escapeHtml(it.waybill)}${it.bookingId ? ` <span style="color:#9a9a9a;">· ${escapeHtml(it.bookingId)}</span>` : ''}</span>`
            )
            .join('')}</div>`
        : ''
    }
  </div>
`;
};

const DispatchMap: React.FC<DispatchMapProps> = ({
  batches,
  deferredBatches = [],
  batchIndexById,
  hub,
  selectedBatchId,
  onSelectBatch,
  height,
  fitPadding = DEFAULT_FIT_PADDING,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const hubMarkerRef = useRef<any>(null);
  // batchId → { markers, polyline, dashed, color } so selection-highlight and
  // teardown can address overlays without rebuilding everything. `dashed` +
  // `color` let the highlight effect restyle a route without losing its
  // pickup-run dash pattern.
  const batchOverlaysRef = useRef<
    Map<
      string,
      { markers: any[]; polyline: any | null; dashed: boolean; color: string }
    >
  >(new Map());
  const infoWindowRef = useRef<any>(null);
  // Keep the latest onSelectBatch in a ref so the map-click listener (bound
  // once at init) always calls the current closure.
  const onSelectBatchRef = useRef(onSelectBatch);
  // Latest routed batches for the hub pin's info window (bound once).
  const batchesRef = useRef(batches);
  batchesRef.current = batches;

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Flips true once `new maps.Map(...)` returns — used as an effect dep so
  // overlay-building effects wait for the map instance before running.
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    onSelectBatchRef.current = onSelectBatch;
  }, [onSelectBatch]);

  // ─── init map once ─────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !containerRef.current) return;
        const map = new maps.Map(containerRef.current, {
          center: JAKARTA_CENTER,
          zoom: JAKARTA_ZOOM,
          styles: GSM_MAP_STYLE,
          disableDefaultUI: false,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          gestureHandling: 'greedy',
        });
        mapRef.current = map;
        // Our content carries its own header, so Google's (with the oversized
        // close button and top padding) is switched off; a map click or the
        // next marker click closes it. Frame styling: `.dispatch-iw` in
        // index.css.
        infoWindowRef.current = new maps.InfoWindow({
          headerDisabled: true,
          minWidth: 272,
        });
        infoWindowRef.current.addListener('domready', () => {
          const iw = document.querySelector('.gm-style-iw-c');
          iw?.classList.add('dispatch-iw');
          iw?.querySelector(`.${IW_CLOSE_CLASS}`)?.addEventListener(
            'click',
            () => infoWindowRef.current?.close()
          );
        });

        // Click on the map background clears the selection — ops can use this
        // as an explicit "show me everything again" gesture without a button.
        map.addListener('click', () => {
          infoWindowRef.current?.close();
          onSelectBatchRef.current?.(null);
        });

        setLoading(false);
        setMapReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : 'Failed to load Google Maps'
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── hub marker ────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const w = window as any;
    const maps = w.google?.maps;
    if (!maps) return;

    if (hubMarkerRef.current) {
      hubMarkerRef.current.setMap(null);
      hubMarkerRef.current = null;
    }
    if (!hub) return;
    const lat = Number(hub.latitude);
    const lng = Number(hub.longitude);
    if (!isValidCoord(lat, lng)) return;

    hubMarkerRef.current = new maps.Marker({
      position: { lat, lng },
      map,
      title: `${hub.code} · ${hub.name}`,
      icon: {
        path: HUB_PIN_PATH,
        scale: 1.1,
        fillColor: '#5E2AAC',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2.5,
        anchor: new maps.Point(0, 0),
      },
      zIndex: 1000,
    });
    // Same info window the stops use; batches come from a ref so the marker
    // isn't rebuilt every time the plan changes.
    hubMarkerRef.current.addListener('click', () => {
      infoWindowRef.current?.setContent(
        buildHubTooltipHTML(hub, batchesRef.current)
      );
      infoWindowRef.current?.setPosition({ lat, lng });
      infoWindowRef.current?.open(map);
    });
  }, [mapReady, hub]);

  // ─── batch overlays (markers + polylines) ──────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const w = window as any;
    const maps = w.google?.maps;
    if (!maps) return;

    // Tear down whatever was rendered last pass. We don't diff per-stop
    // because batches change at the dispatch granularity (PATCH returns a
    // full new tree), so a full rebuild is both simpler and accurate.
    batchOverlaysRef.current.forEach(({ markers, polyline }) => {
      markers.forEach((m) => m.setMap(null));
      polyline?.setMap(null);
    });
    batchOverlaysRef.current.clear();

    const hubLat = hub ? Number(hub.latitude) : NaN;
    const hubLng = hub ? Number(hub.longitude) : NaN;
    const hubCoord = isValidCoord(hubLat, hubLng)
      ? { lat: hubLat, lng: hubLng }
      : null;

    const bounds = new maps.LatLngBounds();
    if (hubCoord) bounds.extend(hubCoord);

    for (const batch of batches) {
      const idx = batchIndexById.get(batch.id) ?? 0;
      const color = batchColor(idx);
      const stops = [...batch.stops].sort((a, b) => a.sequence - b.sequence);
      // A pickup run starts at its first stop and ends at the hub; every
      // other trip departs from the hub (same rule as the BE route). Drives
      // the fallback path order and the dashed + arrowed line style.
      const pickupOnly =
        stops.length > 0 && stops.every((s) => s.kind === 'PICKUP');

      const markers: any[] = [];
      for (const stop of stops) {
        const lat = Number(stop.destinationLat);
        const lng = Number(stop.destinationLong);
        if (!isValidCoord(lat, lng)) continue;
        bounds.extend({ lat, lng });

        const pickup = stop.kind === 'PICKUP';
        const marker = new maps.Marker({
          position: { lat, lng },
          map,
          label: {
            text: String(stop.sequence),
            color: '#ffffff',
            fontSize: '11px',
            fontWeight: '700',
          },
          icon: pickup
            ? {
                path: PICKUP_MARKER_PATH,
                scale: 14,
                fillColor: color,
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 2,
              }
            : {
                path: maps.SymbolPath.CIRCLE,
                scale: 11,
                fillColor: color,
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 2,
              },
          zIndex: 500,
        });

        marker.addListener('click', () => {
          infoWindowRef.current?.setContent(
            buildStopTooltipHTML({
              batchCode: batch.code,
              sequence: stop.sequence,
              pickup,
              riderName: batch.assignedRiderName,
              riderCode: batch.assignedRiderCode,
              clients: (stop.clients ?? []).map((c) => c.name),
              contactName: stop.receiverName,
              address: stop.destinationAddress,
              phone: stop.destinationPhone,
              itemCount: stop.itemCount,
              totalWeightKg: stop.totalWeightKg,
              computedStopTimeMin: stop.computedStopTimeMin,
              legDistanceM: stop.legDistanceM,
              items: stop.items.map((it) => ({
                waybill: it.waybill,
                bookingId: it.bookingId,
              })),
            })
          );
          infoWindowRef.current?.setPosition({ lat, lng });
          infoWindowRef.current?.open(map);
          onSelectBatchRef.current?.(batch.id);
        });

        markers.push(marker);
      }

      // Prefer the snapshotted Mapbox route (real road geometry); fall
      // back to straight hub→stop legs when the polyline is missing (pre-
      // Mapbox row or the snapshot call failed). The fallback keeps the
      // pre-Mapbox visual contract so older dispatches still render.
      let path: any[] = [];
      if (batch.routePolyline && maps.geometry?.encoding?.decodePath) {
        try {
          const latLngs = maps.geometry.encoding.decodePath(batch.routePolyline);
          path = latLngs.map((p: any) => ({ lat: p.lat(), lng: p.lng() }));
        } catch {
          path = [];
        }
      }
      if (path.length < 2) {
        path = [];
        if (hubCoord && !pickupOnly) path.push(hubCoord);
        for (const stop of stops) {
          const lat = Number(stop.destinationLat);
          const lng = Number(stop.destinationLong);
          if (isValidCoord(lat, lng)) path.push({ lat, lng });
        }
        if (hubCoord && pickupOnly) path.push(hubCoord);
      }
      const polyline =
        path.length >= 2
          ? new maps.Polyline({
              path,
              geodesic: false,
              // A pickup run is drawn dashed (see routeStyle); drop runs solid.
              ...routeStyle(maps, color, pickupOnly, 0.85),
              strokeWeight: 2.5,
              map,
              zIndex: 200,
            })
          : null;

      batchOverlaysRef.current.set(batch.id, {
        markers,
        polyline,
        dashed: pickupOnly,
        color,
      });
    }

    // ── DEFERRED ghost stops ────────────────────────────────────────────
    // Route-less, warm-gray hollow markers. No polyline (they aren't routed)
    // and no per-batch color (they're held, not assigned). Stored in the same
    // overlays map (polyline: null) so a click focuses the held batch exactly
    // like a routed one — selection dim + fitBounds come for free.
    for (const batch of deferredBatches) {
      const markers: any[] = [];
      // Number the held stops 1..n (array order) — mirrors the row numbers in
      // the "Ditahan" section so ops can cross-reference marker ↔ list. Held
      // stops aren't routed, so this is a display index, not stop.sequence.
      batch.stops.forEach((stop, i) => {
        const lat = Number(stop.destinationLat);
        const lng = Number(stop.destinationLong);
        if (!isValidCoord(lat, lng)) return;
        bounds.extend({ lat, lng });

        const marker = new maps.Marker({
          position: { lat, lng },
          map,
          label: {
            text: String(i + 1),
            color: DEFERRED_COLOR,
            fontSize: '10px',
            fontWeight: '700',
          },
          icon: {
            path: maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: '#ffffff',
            fillOpacity: 1,
            strokeColor: DEFERRED_COLOR,
            strokeWeight: 3,
          },
          zIndex: 150,
        });

        marker.addListener('click', () => {
          infoWindowRef.current?.setContent(
            buildStopTooltipHTML({
              batchCode: `${batch.code} · DITAHAN`,
              sequence: i + 1,
              pickup: stop.kind === 'PICKUP',
              riderName: null,
              riderCode: null,
              clients: (stop.clients ?? []).map((c) => c.name),
              contactName: stop.receiverName,
              address: stop.destinationAddress,
              phone: stop.destinationPhone,
              itemCount: stop.itemCount,
              totalWeightKg: stop.totalWeightKg,
              computedStopTimeMin: stop.computedStopTimeMin,
              legDistanceM: null,
              items: stop.items.map((it) => ({
                waybill: it.waybill,
                bookingId: it.bookingId,
              })),
            })
          );
          infoWindowRef.current?.setPosition({ lat, lng });
          infoWindowRef.current?.open(map);
          onSelectBatchRef.current?.(batch.id);
        });

        markers.push(marker);
      });
      if (markers.length > 0) {
        batchOverlaysRef.current.set(batch.id, {
          markers,
          polyline: null,
          dashed: false,
          color: DEFERRED_COLOR,
        });
      }
    }

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, fitPadding);
    }
  }, [mapReady, batches, deferredBatches, batchIndexById, hub, fitPadding]);

  // ─── selection highlight ───────────────────────────────────────────────
  // Dim non-selected overlays so the focused batch's route reads cleanly.
  // When selection clears (null), everything snaps back to full opacity.
  useEffect(() => {
    if (!mapReady) return;
    const w = window as any;
    const maps = w.google?.maps;
    if (!maps) return;

    batchOverlaysRef.current.forEach(
      ({ markers, polyline, dashed, color }, batchId) => {
        const active = selectedBatchId === null || selectedBatchId === batchId;
        markers.forEach((m) => m.setOpacity(active ? 1 : 0.25));
        polyline?.setOptions({
          // Through routeStyle, so a dashed pickup run dims via its icons and
          // never gets a solid base stroke painted over its dash pattern.
          ...routeStyle(maps, color, dashed, active ? 0.9 : 0.18),
          strokeWeight: active && selectedBatchId === batchId ? 3.5 : 2.5,
          zIndex: active && selectedBatchId === batchId ? 400 : 200,
        });
      }
    );

    if (selectedBatchId) {
      const overlay = batchOverlaysRef.current.get(selectedBatchId);
      if (overlay && overlay.markers.length > 0) {
        const bounds = new maps.LatLngBounds();
        overlay.markers.forEach((m) => {
          const pos = m.getPosition();
          if (pos) bounds.extend(pos);
        });
        if (hub) {
          const lat = Number(hub.latitude);
          const lng = Number(hub.longitude);
          if (isValidCoord(lat, lng)) bounds.extend({ lat, lng });
        }
        if (!bounds.isEmpty()) {
          mapRef.current?.fitBounds(bounds, fitPadding);
        }
      }
    }
  }, [mapReady, selectedBatchId, hub, fitPadding]);

  // ─── cleanup ───────────────────────────────────────────────────────────
  // Capture refs in locals so the cleanup closure operates on the Maps that
  // existed at mount time (react-hooks/exhaustive-deps guidance).
  useEffect(() => {
    const overlays = batchOverlaysRef.current;
    return () => {
      overlays.forEach(({ markers, polyline }) => {
        markers.forEach((m) => m.setMap(null));
        polyline?.setMap(null);
      });
      overlays.clear();
      if (hubMarkerRef.current) hubMarkerRef.current.setMap(null);
      hubMarkerRef.current = null;
      infoWindowRef.current?.close();
      infoWindowRef.current = null;
      mapRef.current = null;
    };
  }, []);

  return (
    <div
      className={`relative w-full overflow-hidden rounded-lg border border-stroke-soft-200 bg-bg-weak-50${
        height === undefined ? ' h-full' : ''
      }`}
      style={height !== undefined ? { height } : undefined}
    >
      {error ? (
        <div className="flex h-full items-center justify-center px-3 text-center text-[12px] text-red-600">
          <i className="ri-error-warning-line mr-1 text-[14px]" />
          Map unavailable. {error}
        </div>
      ) : (
        <>
          <div ref={containerRef} className="h-full w-full" />
          {loading && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/70 text-[12px] text-text-sub-600">
              Loading map…
            </div>
          )}
          {/* Legend — how a drop run and a pickup run tell apart on the
              canvas: marker shape, line style, and the arrow direction
              relative to the hub. */}
          {!loading && (
            <div className="pointer-events-none absolute left-3 top-3 z-10 flex flex-col gap-1.5 rounded-lg border border-stroke-soft-200 bg-white/95 px-2.5 py-2 text-[10px] leading-none text-text-sub-600 shadow-sm">
              {/* Same glyphs as the canvas: purple teardrop = hub/pitstop,
                  grey circle = dropoff stop, grey diamond = pickup stop. */}
              <span className="flex items-center gap-2">
                <svg width="56" height="14" viewBox="0 0 56 14" aria-hidden="true">
                  <path d="M6 0.5 C3.2 0.5 1.5 2.6 1.5 5 C1.5 8 6 13.5 6 13.5 C6 13.5 10.5 8 10.5 5 C10.5 2.6 8.8 0.5 6 0.5 Z" fill="#5E2AAC" />
                  <circle cx="6" cy="5" r="1.6" fill="#ffffff" />
                  <line x1="14" y1="7" x2="41" y2="7" stroke="#6b7280" strokeWidth="2" />
                  <path d="M41 3 L47 7 L41 11 Z" fill="#6b7280" />
                  <circle cx="51" cy="7" r="4.5" fill="#6b7280" />
                </svg>
                <span>
                  <span className="font-semibold text-text-strong-950">Trip dropoff</span>{' '}
                  · antar dari hub/pitstop ke titik dropoff
                </span>
              </span>
              <span className="flex items-center gap-2">
                <svg width="56" height="14" viewBox="0 0 56 14" aria-hidden="true">
                  <path d="M6 2 L11 7 L6 12 L1 7 Z" fill="#6b7280" />
                  <line x1="14" y1="7" x2="41" y2="7" stroke="#6b7280" strokeWidth="2" strokeDasharray="3 3" />
                  <path d="M41 3 L47 7 L41 11 Z" fill="#6b7280" />
                  <path d="M51 0.5 C48.2 0.5 46.5 2.6 46.5 5 C46.5 8 51 13.5 51 13.5 C51 13.5 55.5 8 55.5 5 C55.5 2.6 53.8 0.5 51 0.5 Z" fill="#5E2AAC" />
                  <circle cx="51" cy="5" r="1.6" fill="#ffffff" />
                </svg>
                <span>
                  <span className="font-semibold text-text-strong-950">Trip pickup</span>{' '}
                  · jemput dari titik pickup ke hub/pitstop
                </span>
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DispatchMap;
